import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SweetAlert from 'react-bootstrap-sweetalert';
import useProjectTowerLayoutV2, { EditableFloorV2, PaletteSvgV2 } from './hooks/useProjectTowerLayoutV2';
import useCreateImageTowerV2 from './hooks/useCreateImageTowerV2';
import { PROJECT_DISTRIBUTION_LIST } from './constants/towerLayoutConstants';
import AdminPage from '../../components/admin/AdminPage';

/* ── Status configurations ── */
const STATUS_COLORS: Record<string, string> = {
  available: '#00b66b',
  coming_soon: '#ff732b',
  sold: '#ea1b2b',
  unavailable: '#757575',
};

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponible',
  coming_soon: 'Próximamente',
  sold: 'Agotado',
  unavailable: 'No Disponible',
};

type ViewMode = 'editor' | 'preview';

const InvestorProjectTowerLayoutV2 = () => {
  const { id = '' } = useParams();

  const {
    towerInfo,
    placedFloors,
    setPlacedFloors,
    svgPalette,
    setSvgPalette,
    selectedFloorId,
    setSelectedFloorId,
    loadTowerLayout,
    handleUploadSvgFile,
    handleSave,
    isLoading,
  } = useProjectTowerLayoutV2();

  const {
    addCurrentImage,
    isLoading: isImageLoading,
  } = useCreateImageTowerV2();

  const isResidential = towerInfo?.layout === 'residential';
  const residentialFloor = placedFloors.find((f) => f.level === 0);

  /* ── UI States ── */
  const [mode, setMode] = useState<ViewMode>('editor');
  const [hoveredFloorId, setHoveredFloorId] = useState<string | null>(null);
  const [activeFloorId, setActiveFloorId] = useState<string | null>(null);
  const [isUploadingSvg, setIsUploadingSvg] = useState<boolean>(false);

  const [canvasWidth, setCanvasWidth] = useState(662);
  const [canvasHeight, setCanvasHeight] = useState(827);
  const [zoom, setZoom] = useState<number>(100);
  const [draggingFloorId, setDraggingFloorId] = useState<string | null>(null);

  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<any>({
    title: '',
    onConfirm: () => setSweetAlert(false),
  });

  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  const handleConfirmSave = () => {
    setAlertProps({
      warning: true,
      title: 'Guardar configuración',
      text: '¿Estás seguro de que deseas guardar la configuración de la torre?',
      showCancel: true,
      confirmBtnBsStyle: 'success',
      confirmBtnText: 'Guardar',
      cancelBtnBsStyle: 'danger',
      cancelBtnText: 'Cancelar',
      onConfirm: async () => {
        setSweetAlert(false);
        const success = await handleSave(placedFloors);
        if (success) {
          setAlertProps({
            success: true,
            title: '¡Guardado!',
            text: 'La configuración de la torre se ha guardado correctamente.',
            confirmBtnBsStyle: 'success',
            confirmBtnText: 'Aceptar',
            onConfirm: () => setSweetAlert(false),
          });
          setSweetAlert(true);
        } else {
          setAlertProps({
            danger: true,
            title: 'Error',
            text: 'Ocurrió un error al guardar la configuración de la torre.',
            confirmBtnBsStyle: 'danger',
            confirmBtnText: 'Entiendo',
            onConfirm: () => setSweetAlert(false),
          });
          setSweetAlert(true);
        }
      },
      onCancel: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  };
  const svgRef = useRef<SVGSVGElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const svgInputRef = useRef<HTMLInputElement>(null);
  const coordsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadTowerLayout();
  }, [loadTowerLayout]);

  // Read natural image dimensions when background loads
  useEffect(() => {
    if (towerInfo?.facade_background) {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 662;
        const h = img.naturalHeight || 827;
        setCanvasWidth(w);
        setCanvasHeight(h);
        const defaultZoom = Math.min(100, Math.round((600 / w) * 100));
        setZoom(defaultZoom);
      };
      img.src = towerInfo.facade_background;
    }
  }, [towerInfo]);

  // SVG Coordinates Conversion Helper
  const clientToSvgCoords = (
    svgEl: SVGSVGElement,
    clientX: number,
    clientY: number,
  ): { x: number; y: number } => {
    const pt = svgEl.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svgEl.getScreenCTM()?.inverse();
    if (!ctm) return { x: 0, y: 0 };
    const svgPt = pt.matrixTransform(ctm);
    return { x: Math.round(svgPt.x * 10) / 10, y: Math.round(svgPt.y * 10) / 10 };
  };

  /* ── Drag & Drop Handlers ── */
  const handlePaletteDragStart = (svgId: string, e: React.DragEvent) => {
    e.dataTransfer.setData('application/x-palette-svg-id', svgId);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const svgId = e.dataTransfer.getData('application/x-palette-svg-id');
    if (!svgId || !svgRef.current) return;

    const paletteSvg = svgPalette.find((s) => s.id === svgId);
    if (!paletteSvg) return;

    const pos = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    const x = pos.x - paletteSvg.width / 2;
    const y = pos.y - paletteSvg.height / 2;

    const levelMatch = paletteSvg.fileName.match(/(\d+)/);
    const level = levelMatch ? parseInt(levelMatch[1], 10) : placedFloors.length + 1;

    const existingFloorIndex = placedFloors.findIndex((f) => f.level === level);

    if (existingFloorIndex !== -1) {
      const existingFloorId = placedFloors[existingFloorIndex].id;
      setPlacedFloors((prev) => {
        const copy = [...prev];
        copy[existingFloorIndex] = {
          ...copy[existingFloorIndex],
          x: Math.round(x * 10) / 10,
          y: Math.round(y * 10) / 10,
          svgWidth: paletteSvg.width || 100,
          svgHeight: paletteSvg.height || 100,
          svgViewBox: paletteSvg.viewBox || '0 0 100 100',
          path: paletteSvg.pathD,
          fileName: paletteSvg.fileName,
          svg_url: paletteSvg.svgUrl,
          placed: true,
        };
        return copy;
      });
      setSelectedFloorId(existingFloorId);
    } else {
      const newFloor: EditableFloorV2 = {
        id: crypto.randomUUID(),
        level,
        x: Math.round(x * 10) / 10,
        y: Math.round(y * 10) / 10,
        svgWidth: paletteSvg.width || 100,
        svgHeight: paletteSvg.height || 100,
        svgViewBox: paletteSvg.viewBox || '0 0 100 100',
        path: paletteSvg.pathD,
        fileName: paletteSvg.fileName,
        status: 'available',
        svg_url: paletteSvg.svgUrl,
        placed: true,
        isNew: true,
      };
      setPlacedFloors((prev) => [...prev, newFloor]);
      setSelectedFloorId(newFloor.id);
    }

    setSvgPalette((prev) => prev.filter((s) => s.id !== svgId));
  };

  const startFloorDrag = (floorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!svgRef.current) return;

    const floor = placedFloors.find((f) => f.id === floorId);
    if (!floor) return;

    const svgPt = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    dragOffset.current = {
      dx: svgPt.x - floor.x,
      dy: svgPt.y - floor.y,
    };

    setDraggingFloorId(floorId);
    setSelectedFloorId(floorId);
  };

  const handleFloorDrag = (e: React.MouseEvent) => {
    if (!draggingFloorId || !svgRef.current) return;

    const svgPt = clientToSvgCoords(svgRef.current, e.clientX, e.clientY);
    const newX = Math.round((svgPt.x - dragOffset.current.dx) * 10) / 10;
    const newY = Math.round((svgPt.y - dragOffset.current.dy) * 10) / 10;

    setPlacedFloors((prev) =>
      prev.map((f) => (f.id === draggingFloorId ? { ...f, x: newX, y: newY } : f)),
    );
  };

  const endFloorDrag = () => {
    setDraggingFloorId(null);
  };

  const handleUpdateProperty = (
    floorId: string,
    key: keyof EditableFloorV2,
    value: any,
  ) => {
    setPlacedFloors((prev) =>
      prev.map((f) => {
        if (f.id !== floorId) return f;
        return { ...f, [key]: value };
      }),
    );
  };

  const handleRemoveFloor = (floorId: string) => {
    const floor = placedFloors.find((f) => f.id === floorId);
    if (!floor) return;

    const paletteSvg: PaletteSvgV2 = {
      id: crypto.randomUUID(),
      fileName: floor.fileName,
      viewBox: floor.svgViewBox,
      width: floor.svgWidth,
      height: floor.svgHeight,
      pathD: floor.path,
      svgUrl: floor.svg_url,
    };

    setSvgPalette((prev) => [...prev, paletteSvg]);
    setPlacedFloors((prev) => prev.filter((f) => f.id !== floorId));
    if (selectedFloorId === floorId) setSelectedFloorId(null);
  };

  const handleSvgInputUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploadingSvg(true);
      try {
        const filesArray = Array.from(e.target.files);
        await Promise.all(filesArray.map((file) => handleUploadSvgFile(file)));
      } catch (error) {
        console.error('Error uploading SVGs:', error);
      } finally {
        setIsUploadingSvg(false);
      }
      e.target.value = '';
    }
  };

  const handleCoordsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const coordsMap = parseCoordinatesFile(text);

      let updatedCount = 0;
      let placedCount = 0;

      // Helper function to normalize keys for comparison
      const normalizeKey = (str: string): string => {
        return str
          .toLowerCase()
          .replace(/\.svg$/i, '')
          .replace(/[\s\-_]+/g, '')
          .replace(/0+(\d+)/g, '$1');
      };

      // Helper to extract a number from a string
      const extractNumber = (str: string): number | null => {
        const match = str.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      };

      // Create a map of normalized coordinate keys and numeric coordinate keys
      const normalizedCoords: Record<string, { x: number; y: number }> = {};
      const numericCoords: Record<number, { x: number; y: number }> = {};

      Object.entries(coordsMap).forEach(([rawKey, coords]) => {
        const norm = normalizeKey(rawKey);
        normalizedCoords[norm] = coords;
        
        const num = extractNumber(rawKey);
        if (num !== null) {
          numericCoords[num] = coords;
        }
      });

      // 1. Update coordinates of already placed floors
      const newPlacedFloors = placedFloors.map((floor) => {
        const fileKeyNorm = normalizeKey(floor.fileName);
        const levelKeyNorm = `nivel${floor.level}`;
        
        // Match by normalized filename, normalized level string, or level number
        const match =
          normalizedCoords[fileKeyNorm] ||
          normalizedCoords[levelKeyNorm] ||
          numericCoords[floor.level];

        if (match) {
          updatedCount++;
          return {
            ...floor,
            x: match.x,
            y: match.y,
          };
        }
        return floor;
      });

      // 2. Find matching SVGs in the palette and place them on the canvas
      const newPalette = [...svgPalette];
      const placedFromPalette: EditableFloorV2[] = [];
      const finalPlacedFloors = [...newPlacedFloors];

      for (let i = newPalette.length - 1; i >= 0; i--) {
        const paletteSvg = newPalette[i];
        const fileKeyNorm = normalizeKey(paletteSvg.fileName);
        const levelNum = extractNumber(paletteSvg.fileName);
        
        // Match by normalized filename or extracted level number
        const match =
          normalizedCoords[fileKeyNorm] ||
          (levelNum !== null ? numericCoords[levelNum] : null);

        if (match) {
          newPalette.splice(i, 1);

          const level = levelNum !== null ? levelNum : finalPlacedFloors.length + placedFromPalette.length + 1;
          const existingIndex = finalPlacedFloors.findIndex((f) => f.level === level);

          if (existingIndex !== -1) {
            updatedCount++;
            finalPlacedFloors[existingIndex] = {
              ...finalPlacedFloors[existingIndex],
              x: match.x,
              y: match.y,
              svgWidth: paletteSvg.width || 100,
              svgHeight: paletteSvg.height || 100,
              svgViewBox: paletteSvg.viewBox || '0 0 100 100',
              path: paletteSvg.pathD,
              fileName: paletteSvg.fileName,
              svg_url: paletteSvg.svgUrl,
              placed: true,
            };
          } else {
            placedCount++;
            placedFromPalette.push({
              id: crypto.randomUUID(),
              level,
              x: match.x,
              y: match.y,
              svgWidth: paletteSvg.width || 100,
              svgHeight: paletteSvg.height || 100,
              svgViewBox: paletteSvg.viewBox || '0 0 100 100',
              path: paletteSvg.pathD,
              fileName: paletteSvg.fileName,
              status: 'available',
              svg_url: paletteSvg.svgUrl,
              placed: true,
              isNew: true,
            });
          }
        }
      }

      setPlacedFloors([...finalPlacedFloors, ...placedFromPalette]);
      setSvgPalette(newPalette);

      alert(`Coordenadas aplicadas:\n- ${updatedCount} niveles actualizados\n- ${placedCount} niveles colocados desde la paleta`);
    } catch (err) {
      console.error(err);
      alert('Error al leer el archivo de coordenadas');
    }

    if (coordsInputRef.current) coordsInputRef.current.value = '';
  };

  const selectedFloor = placedFloors.find((f) => f.id === selectedFloorId);
  const activeFloor = placedFloors.find((f) => f.id === activeFloorId || f.id === hoveredFloorId);

  const cardTotalUnits = activeFloor ? (activeFloor.total_units ?? 0) : 0;
  const cardAvailUnits = activeFloor ? (activeFloor.available_units ?? 0) : 0;

  return (
    <AdminPage
      name={towerInfo?.project_name || 'Cargando...'}
      parentName={isResidential ? 'Administrador de layout de residencial' : 'Administrador de layout de torre'}
      parentUri={'/investors/projects'}
    >
      <div style={styles.wrapper}>
        {sweetAlert && (
          <SweetAlert {...alertProps}>
            {alertProps.text || ''}
          </SweetAlert>
        )}
        {/* ── Top Header ── */}
        <header style={styles.header}>
          {/* Mode Toggle */}
          <div style={styles.modeToggle}>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === 'editor' ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode('editor')}
            >
              ✏️ Editor
            </button>
            <button
              style={{
                ...styles.modeBtn,
                ...(mode === 'preview' ? styles.modeBtnActive : {}),
              }}
              onClick={() => setMode('preview')}
            >
              👁️ Preview
            </button>
          </div>

          {/* Zoom Slider */}
          {towerInfo?.facade_background && (
            <div style={styles.zoomContainer}>
              <span style={styles.zoomLabel}>🔍 Zoom: {zoom}%</span>
              <input
                type="range"
                min="10"
                max="100"
                step="5"
                value={zoom}
                onChange={(e) => setZoom(parseInt(e.target.value, 10))}
                style={styles.zoomSlider}
              />
            </div>
          )}

          {/* Header Save Action */}
          <div style={styles.headerActions}>
            <button
              style={styles.saveBtn}
              onClick={handleConfirmSave}
              disabled={isLoading || isImageLoading || !towerInfo?.facade_background}
            >
              {isLoading ? 'Guardando...' : '💾 Guardar'}
            </button>
          </div>
        </header>

        {/* ── Main Viewport ── */}
        {!towerInfo?.facade_background ? (
          <div style={styles.emptyStateContainer}>
            <div style={styles.emptyIcon}>{isResidential ? '🏡' : '🏢'}</div>
            <div style={styles.emptyTitle}>
              {isResidential ? 'Sube la imagen de plano residencial' : 'Sube la imagen de fondo de la torre'}
            </div>
            <div style={styles.emptySub}>
              {isResidential
                ? 'Por favor sube la imagen principal del plano residencial para empezar.'
                : 'Por favor sube la imagen principal del edificio para empezar.'}
            </div>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="bg-upload-main"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const formData = new FormData();
                  formData.append('cover', file);
                  addCurrentImage(formData);
                }
              }}
            />
            <button
              style={styles.uploadMainBtn}
              onClick={() => document.getElementById('bg-upload-main')?.click()}
            >
              Subir Imagen
            </button>
          </div>
        ) : (
          <div style={styles.editorBody}>
            {/* ══════════════════════════════════════════════
              EDITOR MODE
              ══════════════════════════════════════════════ */}
            {mode === 'editor' && (
              <>
                {/* Left Sidebar Palette */}
                <aside style={styles.sidebarLeft}>
                  {/* Background image preview & change */}
                  <div style={styles.sidebarSection}>
                    <div style={styles.sectionTitle}>
                      {isResidential ? '🖼️ Plano Residencial' : '🖼️ Imagen de torre'}
                    </div>
                    <div style={styles.thumbWrap}>
                      <img src={towerInfo.facade_background} alt={isResidential ? 'Residencial' : 'Torre'} style={styles.thumbImg} />
                      <button
                        style={styles.changeBtn}
                        onClick={() => bgInputRef.current?.click()}
                      >
                        Cambiar
                      </button>
                      <input
                        ref={bgInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('cover', file);
                            addCurrentImage(formData);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* SVG Upload & Palette - Omit if residential */}
                  {!isResidential && (
                    <>
                      {/* SVG Upload */}
                      <div style={styles.sidebarSection}>
                        <div style={styles.sectionTitle}>📐 SVGs de niveles</div>
                        <input
                          ref={svgInputRef}
                          type="file"
                          accept=".svg"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handleSvgInputUpload}
                        />
                        <input
                          ref={coordsInputRef}
                          type="file"
                          accept=".txt"
                          style={{ display: 'none' }}
                          onChange={handleCoordsUpload}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <button
                            style={{
                              ...styles.uploadBtn,
                              opacity: isUploadingSvg ? 0.7 : 1,
                              cursor: isUploadingSvg ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => !isUploadingSvg && svgInputRef.current?.click()}
                            disabled={isUploadingSvg}
                          >
                            {isUploadingSvg ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                  style={{ width: '14px', height: '14px', marginRight: '6px' }}
                                />
                                Subiendo SVGs...
                              </>
                            ) : (
                              <>
                                <span style={styles.uploadIcon}>+</span>
                                Subir archivos SVG
                              </>
                            )}
                          </button>
                          <button
                            style={{
                              ...styles.uploadBtn,
                              border: '2px dashed #d0d0d0',
                            }}
                            onClick={() => coordsInputRef.current?.click()}
                          >
                            🗺️ Cargar Coordenadas (.txt)
                          </button>
                        </div>
                      </div>

                      {/* SVG Palette Items */}
                      {svgPalette.length > 0 && (
                        <div style={{ ...styles.sidebarSection, flex: 1, overflowY: 'auto' }}>
                          <div style={styles.sectionTitle}>Arrastra al lienzo ({svgPalette.length})</div>
                          <div style={styles.svgList}>
                            {svgPalette.map((svg) => (
                              <div
                                key={svg.id}
                                draggable
                                onDragStart={(e) => handlePaletteDragStart(svg.id, e)}
                                style={styles.svgItem}
                              >
                                <svg viewBox={svg.viewBox} style={styles.svgMini}>
                                  <path d={svg.pathD} fill="#00b66b" fillOpacity={0.4} stroke="#00b66b" strokeWidth={1.5} />
                                </svg>
                                <div style={styles.svgItemInfo}>
                                  <span style={styles.svgItemName}>{svg.fileName}</span>
                                  <span style={styles.svgItemDim}>{svg.width}×{svg.height}</span>
                                </div>
                                <button
                                  style={styles.removePaletteBtn}
                                  onClick={() => setSvgPalette((prev) => prev.filter((s) => s.id !== svg.id))}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </aside>

                {/* Canvas Center */}
                <div
                  style={styles.canvasWrap}
                  onDragOver={isResidential ? undefined : handleCanvasDragOver}
                  onDrop={isResidential ? undefined : handleCanvasDrop}
                >
                  <div style={styles.svgContainer}>
                    <svg
                      ref={svgRef}
                      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{
                        ...styles.canvasSvg,
                        cursor: draggingFloorId ? 'grabbing' : 'default',
                        width: `${(canvasWidth * zoom) / 100}px`,
                        height: `${(canvasHeight * zoom) / 100}px`,
                      }}
                      onMouseMove={isResidential ? undefined : handleFloorDrag}
                      onMouseUp={isResidential ? undefined : endFloorDrag}
                      onMouseLeave={isResidential ? undefined : endFloorDrag}
                      onClick={isResidential ? undefined : () => setSelectedFloorId(null)}
                    >
                      <image
                        href={towerInfo.facade_background}
                        x={0}
                        y={0}
                        width={canvasWidth}
                        height={canvasHeight}
                        preserveAspectRatio="none"
                      />

                      {/* Placed SVG floors */}
                      {!isResidential && placedFloors.filter((floor) => floor.placed).map((floor) => {
                        const isSelected = selectedFloorId === floor.id;
                        const color = STATUS_COLORS[floor.status] || '#00b66b';
                        return (
                          <svg
                            key={floor.id}
                            x={floor.x}
                            y={floor.y}
                            width={floor.svgWidth}
                            height={floor.svgHeight}
                            viewBox={floor.svgViewBox}
                            preserveAspectRatio="none"
                            overflow="visible"
                          >
                            <rect
                              x={0}
                              y={0}
                              width={floor.svgWidth}
                              height={floor.svgHeight}
                              fill="transparent"
                              style={{ cursor: draggingFloorId === floor.id ? 'grabbing' : 'grab' }}
                              onMouseDown={(e) => startFloorDrag(floor.id, e)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFloorId(floor.id);
                              }}
                            />
                            <path
                              d={floor.path}
                              fill={color}
                              fillOpacity={isSelected ? 0.45 : 0.2}
                              stroke={color}
                              strokeWidth={1.2}
                              style={{
                                pointerEvents: 'none',
                                filter: isSelected ? `drop-shadow(0 0 6px ${color}99)` : 'none',
                              }}
                            />
                            <text
                              x={floor.svgWidth / 2}
                              y={floor.svgHeight / 2 + 4}
                              textAnchor="middle"
                              fontSize={Math.min(floor.svgWidth, floor.svgHeight) * 0.3}
                              fontWeight={800}
                              fill={isSelected ? '#212121' : color}
                              fillOpacity={0.7}
                              style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                              {floor.level}
                            </text>
                          </svg>
                        );
                      })}
                    </svg>
                  </div>
                </div>

                {/* Right Sidebar properties */}
                <aside style={styles.sidebarRight}>
                  {isResidential ? (
                    <div style={styles.sidebarSection}>
                      <div style={styles.sectionTitle}>Distribución Residencial</div>
                      <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
                        Este proyecto está configurado en <strong>Modo Residencial</strong>. No es necesario colocar niveles o pisos.
                      </p>
                      <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.5', marginTop: '10px' }}>
                        Para empezar a plotiar y asignar las unidades del proyecto sobre este plano, haz clic en el botón inferior.
                      </p>
                      
                      {residentialFloor ? (
                        <div style={{ marginTop: '24px' }}>
                          <Link
                            to={`/investors/projects/${id}/tower-layout-v2/floor-layout-v2/${residentialFloor.id}`}
                            className="btn btn-success btn-block"
                            style={{
                              textAlign: 'center',
                              fontWeight: 700,
                              fontSize: '12px',
                              borderRadius: '8px',
                              padding: '12px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              background: '#00b66b',
                              borderColor: '#00b66b',
                              color: '#ffffff',
                              display: 'block',
                            }}
                          >
                            Asignar polígonos a nivel
                          </Link>
                        </div>
                      ) : (
                        <div style={{ color: '#ff732b', fontSize: '13px', marginTop: '20px', fontWeight: 600 }}>
                          ⚠️ Guarda la configuración para activar la asignación de polígonos.
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div style={styles.sidebarSection}>
                        <div style={styles.sectionTitle}>Niveles colocados ({placedFloors.filter((f) => f.placed).length})</div>
                        <div style={styles.floorListWrap}>
                          {placedFloors.filter((f) => f.placed).map((f) => {
                            const isSel = selectedFloorId === f.id;
                            const color = STATUS_COLORS[f.status] || '#00b66b';
                            return (
                              <button
                                key={f.id}
                                onClick={() => setSelectedFloorId(isSel ? null : f.id)}
                                style={{
                                  ...styles.floorItemBtn,
                                  background: isSel ? `${color}18` : 'transparent',
                                  borderLeft: `3px solid ${isSel ? color : 'transparent'}`,
                                }}
                              >
                                <span style={{ ...styles.floorItemDot, background: color }} />
                                <span style={styles.floorItemLabel}>Nivel {f.level}</span>
                                <span style={styles.floorItemPos}>({Math.round(f.x)}, {Math.round(f.y)})</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {selectedFloor ? (
                        <div style={styles.sidebarSection}>
                          <div style={styles.propsHeader}>
                            <div style={styles.propsLabel}>Propiedades</div>
                            <div style={{ ...styles.propsFloor, color: STATUS_COLORS[selectedFloor.status] || '#00b66b' }}>
                              Nivel {selectedFloor.level} · {selectedFloor.fileName}
                            </div>
                          </div>

                          {/* Level input */}
                          <div style={styles.propRowContainer}>
                            <span style={styles.propRowLabel}>Nivel</span>
                            <input
                              type="number"
                              style={styles.propRowInput}
                              value={selectedFloor.level}
                              onChange={(e) => handleUpdateProperty(selectedFloor.id, 'level', parseInt(e.target.value) || 0)}
                            />
                          </div>

                          {/* Status input */}
                          <div style={styles.propRowContainer}>
                            <span style={styles.propRowLabel}>Estado</span>
                            <select
                              style={styles.propRowInput}
                              value={selectedFloor.status}
                              onChange={(e) => handleUpdateProperty(selectedFloor.id, 'status', e.target.value)}
                            >
                              {PROJECT_DISTRIBUTION_LIST.map((dist) => (
                                <option key={dist.value} value={dist.value}>
                                  {dist.display_value}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sliders */}
                          <PropRow
                            label="Posición X"
                            value={selectedFloor.x}
                            min={-100}
                            max={canvasWidth}
                            step={5}
                            onChange={(v) => handleUpdateProperty(selectedFloor.id, 'x', v)}
                            color={STATUS_COLORS[selectedFloor.status]}
                          />

                          <PropRow
                            label="Posición Y"
                            value={selectedFloor.y}
                            min={-100}
                            max={canvasHeight}
                            step={5}
                            onChange={(v) => handleUpdateProperty(selectedFloor.id, 'y', v)}
                            color={STATUS_COLORS[selectedFloor.status]}
                          />

                          <Link
                            to={`/investors/projects/${id}/tower-layout-v2/floor-layout-v2/${selectedFloor.id}`}
                            className="btn btn-success btn-block"
                            style={{
                              marginTop: '16px',
                              marginBottom: '10px',
                              display: 'block',
                              textAlign: 'center',
                              fontWeight: 700,
                              fontSize: '12px',
                              borderRadius: '8px',
                              padding: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              background: '#00b66b',
                              borderColor: '#00b66b',
                              color: '#ffffff',
                            }}
                          >
                            Asignar polígonos a nivel
                          </Link>

                          <button style={styles.removeFloorBtn} onClick={() => handleRemoveFloor(selectedFloor.id)}>
                            🗑️ Quitar del lienzo
                          </button>
                        </div>
                      ) : (
                        <div style={styles.emptyHint}>
                          Selecciona un nivel en el lienzo o en la lista superior para configurar sus propiedades.
                        </div>
                      )}
                    </>
                  )}
                </aside>
              </>
            )}

            {/* ══════════════════════════════════════════════
              PREVIEW MODE (TowerViewer V2)
              ══════════════════════════════════════════════ */}
            {mode === 'preview' && (
              <div style={styles.viewerContainer}>
                {/* Building + SVGs layout */}
                <div style={styles.viewerMain}>
                  <div style={styles.viewerBuildingContainer}>
                    <svg
                      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{
                        ...styles.viewerSvg,
                        width: `${(canvasWidth * zoom) / 100}px`,
                        height: `${(canvasHeight * zoom) / 100}px`,
                      }}
                    >
                      <image
                        href={towerInfo.facade_background}
                        x={0}
                        y={0}
                        width={canvasWidth}
                        height={canvasHeight}
                        preserveAspectRatio="none"
                      />
                      {!isResidential && placedFloors.filter((floor) => floor.placed).map((floor) => {
                        const isHover = hoveredFloorId === floor.id;
                        const isActive = activeFloorId === floor.id;
                        const color = STATUS_COLORS[floor.status] || '#00b66b';
                        const highlight = isHover || isActive;

                        return (
                          <svg
                            key={floor.id}
                            x={floor.x}
                            y={floor.y}
                            width={floor.svgWidth}
                            height={floor.svgHeight}
                            viewBox={floor.svgViewBox}
                            preserveAspectRatio="none"
                            overflow="visible"
                          >
                            <path
                              d={floor.path}
                              fill={color}
                              fillOpacity={highlight ? 0.38 : 0.1}
                              stroke={color}
                              strokeOpacity={highlight ? 1 : 0.25}
                              strokeWidth={highlight ? 2 : 0.8}
                              style={{
                                cursor: 'pointer',
                                transition: 'fill-opacity 0.25s ease, stroke-opacity 0.25s ease',
                                filter: highlight ? `drop-shadow(0 0 5px ${color}99)` : 'none',
                              }}
                              onMouseEnter={() => setHoveredFloorId(floor.id)}
                              onMouseLeave={() => setHoveredFloorId(null)}
                              onClick={() => setActiveFloorId((prev) => (prev === floor.id ? null : floor.id))}
                            />
                          </svg>
                        );
                      })}
                    </svg>

                    {/* Floor Info Card in Preview */}
                    {!isResidential && activeFloor && (
                      <div style={styles.infoCard}>
                        <div style={styles.infoCardHeader}>
                          <div>
                            <div style={styles.infoCardLevelLabel}>Nivel</div>
                            <div style={styles.infoCardLevelNum}>{activeFloor.level}</div>
                          </div>
                          <button
                            onClick={() => {
                              setActiveFloorId(null);
                              setHoveredFloorId(null);
                            }}
                            style={styles.infoCardClose}
                          >
                            ✕
                          </button>
                        </div>

                        <div style={styles.infoCardStatRow}>
                          <span style={styles.infoCardStatLabel}>Unidades</span>
                          <span style={styles.infoCardStatValue}>
                            {cardAvailUnits}/{cardTotalUnits}
                          </span>
                        </div>

                        <Link
                          to={`/investors/projects/${id}/tower-layout-v2/floor-layout-v2/${activeFloor.id}`}
                          className="btn btn-success btn-sm btn-block"
                          style={{
                            marginTop: '12px',
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: '11px',
                            borderRadius: '6px',
                            padding: '8px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            background: '#00b66b',
                            borderColor: '#00b66b',
                            color: '#ffffff',
                            display: 'block',
                          }}
                        >
                          Asignar polígonos a nivel
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview Right List */}
                <aside style={styles.viewerSide}>
                  {isResidential ? (
                    <>
                      <div style={styles.viewerSideTitle}>Vista Residencial</div>
                      <div style={{ padding: '20px', fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
                        <p>Esta es la vista previa de la distribución del plano residencial.</p>
                        <p style={{ marginTop: '10px' }}>
                          Las unidades están mapeadas directamente sobre este lienzo.
                        </p>
                        {residentialFloor && (
                          <div style={{ marginTop: '20px' }}>
                            <Link
                              to={`/investors/projects/${id}/tower-layout-v2/floor-layout-v2/${residentialFloor.id}`}
                              className="btn btn-success btn-sm btn-block"
                              style={{
                                textAlign: 'center',
                                fontWeight: 700,
                                borderRadius: '6px',
                                padding: '10px',
                                background: '#00b66b',
                                borderColor: '#00b66b',
                                color: '#ffffff',
                                display: 'block',
                              }}
                            >
                              Ver Unidades Mapeadas
                            </Link>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.viewerSideTitle}>Niveles</div>
                      <div style={styles.viewerSideList}>
                        {placedFloors.filter((floor) => floor.placed).map((floor) => {
                          const color = STATUS_COLORS[floor.status] || '#00b66b';
                          const isSelected = activeFloorId === floor.id;
                          const isHov = hoveredFloorId === floor.id;
                          return (
                            <button
                              key={floor.id}
                              style={{
                                ...styles.viewerSideItem,
                                background: isSelected ? `${color}18` : isHov ? '#f5f5f5' : 'transparent',
                                borderLeft: `3px solid ${isSelected || isHov ? color : 'transparent'}`,
                              }}
                              onMouseEnter={() => setHoveredFloorId(floor.id)}
                              onMouseLeave={() => setHoveredFloorId(null)}
                              onClick={() => setActiveFloorId((prev) => (prev === floor.id ? null : floor.id))}
                            >
                              <span style={{ ...styles.viewerSideDot, background: color }} />
                              <div style={styles.viewerSideText}>
                                <span style={styles.viewerSideLevelNum}>Nivel {floor.level}</span>
                                <span style={styles.viewerSideStatus}>
                                  {floor.status === 'sold'
                                    ? 'Agotado'
                                    : `${floor.available_units ?? 0}/${floor.total_units ?? 0} disp.`}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </aside>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminPage>
  );
};

/* ── Coordinates Parser Helper ── */
const parseCoordinatesFile = (text: string): Record<string, { x: number; y: number }> => {
  const result: Record<string, { x: number; y: number }> = {};
  const lines = text.split(/\r?\n/);
  const coordRegex = /(?:[xy][:=]?)?\s*([-\d.]+)\s+(?:[xy][:=]?)?\s*([-\d.]+)\s*$/i;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    const coordMatch = trimmed.match(coordRegex);
    if (coordMatch) {
      const x = parseFloat(coordMatch[1]);
      const y = parseFloat(coordMatch[2]);
      if (!isNaN(x) && !isNaN(y)) {
        const idPart = trimmed.substring(0, coordMatch.index).trim();
        const cleanId = idPart.replace(/\s*(?:[xy][:=]?)?\s*$/, '').trim();
        if (cleanId) {
          result[cleanId.toLowerCase()] = { x, y };
        }
      }
    }
  }
  return result;
};

/* ── PropRow component (inline helper) ── */
interface PropRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: number) => void;
  color?: string;
}

const PropRow: React.FC<PropRowProps> = ({
  label,
  value,
  min,
  max,
  step,
  unit = 'px',
  onChange,
  color = '#00b66b',
}) => {
  const [inputValue, setInputValue] = useState<string>(String(Math.round(value * 10) / 10));

  useEffect(() => {
    setInputValue(String(Math.round(value * 10) / 10));
  }, [value]);

  const nudge = (delta: number) => {
    const next = Math.round((value + delta) * 10) / 10;
    const bounded = Math.max(min, Math.min(max, next));
    onChange(bounded);
    setInputValue(String(bounded));
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed)) {
      onChange(Math.max(min, Math.min(max, parsed)));
    }
  };

  const handleBlur = () => {
    setInputValue(String(Math.round(value * 10) / 10));
  };

  return (
    <div style={propStyles.row}>
      <div style={propStyles.labelRow}>
        <span style={propStyles.label}>{label}</span>
        <span style={propStyles.valueDisplay}>
          {Math.round(value * 10) / 10}
          <span style={propStyles.unit}>{unit}</span>
        </span>
      </div>

      <div style={propStyles.sliderRow}>
        <button type="button" style={propStyles.nudgeBtn} onClick={() => nudge(-step)}>
          ◀
        </button>
        <input
          type="range"
          min={min}
          max={max}
          step={step * 0.1}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          style={{ ...propStyles.slider, accentColor: color }}
        />
        <button type="button" style={propStyles.nudgeBtn} onClick={() => nudge(step)}>
          ▶
        </button>
      </div>

      <input
        type="number"
        min={min}
        max={max}
        step={step * 0.1}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        style={propStyles.numInput}
      />
    </div>
  );
};

/* ── Inline CSS Styles ── */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
    minHeight: '650px',
    background: '#f5f5f5',
    fontFamily: "'Nunito', 'Segoe UI', sans-serif",
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '52px',
    padding: '0 24px',
    background: '#ffffff',
    borderBottom: '1px solid #eeeeee',
    flexShrink: 0,
    boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 800,
    fontSize: '16px',
    color: '#00b66b',
  },
  logoIcon: {
    width: '26px',
    height: '26px',
    background: '#e5fff5',
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeToggle: {
    display: 'flex',
    background: '#f0f0f0',
    borderRadius: '8px',
    padding: '2px',
    gap: '2px',
  },
  modeBtn: {
    border: 'none',
    borderRadius: '6px',
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    color: '#757575',
    transition: 'all 0.2s',
  },
  modeBtnActive: {
    background: '#ffffff',
    color: '#212121',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  saveBtn: {
    border: '1px solid #00b66b',
    borderRadius: '8px',
    padding: '5px 16px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    background: '#00b66b',
    color: '#ffffff',
    transition: 'background 0.2s',
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: '#9e9e9e',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.5,
    marginBottom: '10px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#616161',
    marginBottom: '5px',
  },
  emptySub: {
    fontSize: '14px',
    maxWidth: '300px',
    lineHeight: 1.5,
    marginBottom: '20px',
  },
  uploadMainBtn: {
    padding: '10px 24px',
    background: '#00b66b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  editorBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  sidebarLeft: {
    width: '230px',
    flexShrink: 0,
    background: '#ffffff',
    borderRight: '1px solid #eeeeee',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarSection: {
    padding: '14px 16px 10px',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    marginBottom: '10px',
  },
  thumbWrap: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #eeeeee',
  },
  thumbImg: {
    width: '100%',
    height: '100px',
    objectFit: 'cover',
    display: 'block',
  },
  changeBtn: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '10px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  uploadBtn: {
    width: '100%',
    padding: '12px 10px',
    background: '#f5f5f5',
    border: '2px dashed #d0d0d0',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    color: '#616161',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'border-color 0.2s, background 0.2s',
  },
  uploadIcon: {
    fontSize: '18px',
    fontWeight: 800,
    color: '#00b66b',
    lineHeight: 1,
  },
  svgList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  svgItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '8px',
    background: '#f9f9f9',
    border: '1px solid #eeeeee',
    cursor: 'grab',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  },
  svgMini: {
    width: '36px',
    height: '24px',
    flexShrink: 0,
    background: '#f0f0f0',
    borderRadius: '4px',
  },
  svgItemInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
  },
  svgItemName: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#212121',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  svgItemDim: {
    fontSize: '10px',
    color: '#9e9e9e',
    fontFamily: 'monospace',
  },
  removePaletteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#bdbdbd',
    padding: '2px 4px',
    lineHeight: 1,
  },
  canvasWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    overflowY: 'auto',
    overflowX: 'auto',
    background: '#eaeaea',
    position: 'relative',
    height: '100%',
  },
  svgContainer: {
    position: 'relative',
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  canvasSvg: {
    display: 'block',
    borderRadius: '4px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    background: '#fff',
  },
  infoBadge: {
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: '20px',
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
  },
  sidebarRight: {
    width: '280px',
    flexShrink: 0,
    background: '#ffffff',
    borderLeft: '1px solid #eeeeee',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  floorListWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '180px',
    overflowY: 'auto',
    border: '1px solid #eeeeee',
    borderRadius: '4px',
  },
  floorItemBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    transition: 'background 0.15s',
  },
  floorItemDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  floorItemLabel: {
    fontWeight: 700,
    color: '#212121',
    flex: 1,
  },
  floorItemPos: {
    fontSize: '10px',
    color: '#9e9e9e',
    fontFamily: 'monospace',
  },
  propsHeader: {
    marginBottom: '14px',
  },
  propsLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
  },
  propsFloor: {
    fontSize: '14px',
    fontWeight: 800,
    marginTop: '2px',
  },
  propRowContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    gap: '8px',
  },
  propRowLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#616161',
    textTransform: 'uppercase',
  },
  propRowInput: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '12px',
    outline: 'none',
    background: '#fafafa',
  },
  removeFloorBtn: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ffcdd2',
    borderRadius: '8px',
    background: '#fff5f5',
    color: '#d32f2f',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '14px',
    transition: 'background 0.15s',
  },
  emptyHint: {
    fontSize: '12px',
    color: '#bdbdbd',
    textAlign: 'center',
    padding: '24px 16px',
    lineHeight: 1.5,
  },
  /* Viewer Styles */
  viewerContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  viewerMain: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: '24px',
    overflowY: 'auto',
    overflowX: 'auto',
    background: '#eaeaea',
  },
  viewerBuildingContainer: {
    position: 'relative',
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerSvg: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  viewerSide: {
    width: '200px',
    flexShrink: 0,
    background: '#ffffff',
    borderLeft: '1px solid #eeeeee',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    gap: '2px',
  },
  viewerSideTitle: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    padding: '0 16px 10px',
  },
  viewerSideList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  viewerSideItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 16px',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 0.15s, border-left-color 0.15s',
    borderLeft: '3px solid transparent',
  },
  viewerSideDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  viewerSideText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  viewerSideLevelNum: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#212121',
  },
  viewerSideStatus: {
    fontSize: '11px',
    color: '#757575',
  },
  infoCard: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '220px',
    background: '#ffffff',
    borderRadius: '14px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
    padding: '20px 18px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 10,
  },
  infoCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  infoCardLevelLabel: {
    fontSize: '11px',
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  infoCardLevelNum: {
    fontSize: '36px',
    fontWeight: 800,
    color: '#212121',
    lineHeight: 1,
  },
  infoCardClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#9e9e9e',
    padding: '4px',
    lineHeight: 1,
  },
  infoCardBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    alignSelf: 'flex-start',
  },
  infoCardDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  infoCardStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  infoCardStatLabel: {
    fontSize: '12px',
    color: '#757575',
  },
  infoCardStatValue: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#212121',
  },
  barTrack: {
    height: '6px',
    background: '#eeeeee',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '-4px',
    marginBottom: '4px',
  },
  barFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  },
  soldMsg: {
    fontSize: '13px',
    color: '#9e9e9e',
    textAlign: 'center',
    padding: '8px 0',
  },
  zoomContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#f9f9f9',
    padding: '4px 12px',
    borderRadius: '8px',
    border: '1px solid #eeeeee',
  },
  zoomLabel: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#616161',
    minWidth: '90px',
    userSelect: 'none',
  },
  zoomSlider: {
    cursor: 'pointer',
    width: '120px',
  },
};

const propStyles: Record<string, React.CSSProperties> = {
  row: {
    marginBottom: '14px',
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '4px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  valueDisplay: {
    fontSize: '14px',
    fontWeight: 800,
    color: '#212121',
    fontFamily: 'monospace',
  },
  unit: {
    fontSize: '10px',
    fontWeight: 400,
    color: '#9e9e9e',
    marginLeft: '2px',
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '4px',
  },
  slider: {
    flex: 1,
    height: '4px',
    cursor: 'pointer',
  },
  nudgeBtn: {
    width: '24px',
    height: '24px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    background: '#fafafa',
    cursor: 'pointer',
    fontSize: '10px',
    color: '#616161',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
  numInput: {
    width: '100%',
    padding: '5px 8px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: 'monospace',
    fontWeight: 600,
    color: '#212121',
    background: '#fafafa',
    outline: 'none',
  },
};

export default InvestorProjectTowerLayoutV2;
